'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import {
  buildCardSvg,
  buildVCard,
  collectBrandFontCss,
  downloadCardPdf,
  downloadCardPng,
  downloadVCard,
  fetchAsDataUrl,
  CARD_W,
  CARD_H,
} from '@/lib/digitalCard';
import { CONTACT } from '@/lib/contact';

type Busy = 'png' | 'pdf' | 'vcf' | null;

const socialLinks = CONTACT.socials;

const DigitalCard: React.FC = () => {
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [qrMatrix, setQrMatrix] = useState<boolean[][] | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const fontCssRef = useRef<string | null>(null);

  // Inline the logo + build the vCard QR once on mount (client-only).
  useEffect(() => {
    let cancelled = false;

    fetchAsDataUrl('/assets/images/logo.png')
      .then((url) => !cancelled && setLogoDataUrl(url))
      .catch(() => {
        /* card still renders without the logo */
      });

    import('qrcode')
      .then(({ default: QRCode }) => {
        const qr = QRCode.create(buildVCard(), { errorCorrectionLevel: 'M' });
        const size = qr.modules.size;
        const data = qr.modules.data;
        const matrix: boolean[][] = [];
        for (let r = 0; r < size; r++) {
          const row: boolean[] = [];
          for (let c = 0; c < size; c++) row.push(!!data[r * size + c]);
          matrix.push(row);
        }
        if (!cancelled) setQrMatrix(matrix);
      })
      .catch(() => {
        /* card still renders without the QR */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const ready = logoDataUrl !== null && qrMatrix !== null;
  const previewSvg = ready ? buildCardSvg({ logoDataUrl, qrMatrix, mode: 'preview' }) : null;

  async function handleDownload(kind: 'png' | 'pdf') {
    if (busy || !ready) return;
    setBusy(kind);
    setError(null);
    try {
      if (fontCssRef.current === null) {
        fontCssRef.current = await collectBrandFontCss();
      }
      const svg = buildCardSvg({
        logoDataUrl,
        qrMatrix,
        mode: 'export',
        fontFaceCss: fontCssRef.current,
      });
      if (kind === 'png') await downloadCardPng(svg);
      else await downloadCardPdf(svg);
    } catch {
      setError('Sorry, the download failed. Please try again.');
    } finally {
      setBusy(null);
    }
  }

  function handleSaveContact() {
    if (busy) return;
    setBusy('vcf');
    setError(null);
    try {
      downloadVCard();
    } catch {
      setError('Sorry, the download failed. Please try again.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section id="digital-card" className="py-12 lg:py-16 bg-muted/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          {/* "Stay in Touch", not "Get in Touch" - the sidebar above already
              uses that for the enquiry form, and this section is about keeping
              the connection (save our details, follow us) rather than starting
              one. */}
          <p className="font-cta text-sm tracking-wider text-accent uppercase mb-2">
            Stay in Touch
          </p>
          <h2 className="font-headline text-2xl md:text-3xl font-bold text-foreground mb-3">
            Save Flora Dine to your contacts
          </h2>
          <p className="font-body text-lg text-muted-foreground leading-relaxed">
            Scan the card to add us instantly, or download it as an image or PDF to share with your
            team and procurement partners.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center max-w-5xl mx-auto">
          {/* Card preview */}
          <div className="flex justify-center">
            <div className="w-full max-w-[360px]">
              {previewSvg ? (
                <div
                  className="rounded-[31px] shadow-dramatic [&>svg]:block [&>svg]:w-full [&>svg]:h-auto"
                  // SVG is built from trusted, static brand data in this codebase.
                  dangerouslySetInnerHTML={{ __html: previewSvg }}
                />
              ) : (
                <div
                  className="w-full rounded-[31px] bg-muted animate-pulse"
                  style={{ aspectRatio: `${CARD_W} / ${CARD_H}` }}
                  aria-hidden="true"
                />
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="text-center lg:text-left">
            <h3 className="font-headline text-xl font-bold text-foreground mb-2">
              Download the card
            </h3>
            <p className="font-body text-muted-foreground mb-6">
              The QR code holds our full contact details - phone, email, website and address - so a
              single scan saves Flora Dine straight to your phone.
            </p>

            <div className="flex flex-col sm:flex-row lg:flex-col gap-3 max-w-sm mx-auto lg:mx-0">
              <button
                type="button"
                onClick={() => handleDownload('png')}
                disabled={!ready || busy !== null}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[44px] rounded-xl bg-primary text-primary-foreground font-cta font-semibold transition hover:bg-primary/90 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="ArrowDownTrayIcon" size={20} className="shrink-0" />
                {busy === 'png' ? 'Preparing image…' : 'Download image (PNG)'}
              </button>

              <button
                type="button"
                onClick={() => handleDownload('pdf')}
                disabled={!ready || busy !== null}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[44px] rounded-xl bg-surface border border-border text-foreground font-cta font-semibold transition hover:bg-muted active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="DocumentTextIcon" size={20} className="shrink-0 text-primary" />
                {busy === 'pdf' ? 'Preparing PDF…' : 'Download PDF'}
              </button>

              <button
                type="button"
                onClick={handleSaveContact}
                disabled={busy !== null}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[44px] rounded-xl text-primary font-cta font-semibold transition hover:bg-primary/10 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="ArrowDownTrayIcon" size={20} className="shrink-0" />
                Save contact (.vcf)
              </button>
            </div>

            {error && (
              <p className="font-body text-sm text-error mt-4" role="alert">
                {error}
              </p>
            )}

            {/* Following us is the lighter, optional action, so it sits below
                the download CTA and behind a divider rather than in a bordered
                card of its own - a boxed panel above the buttons made the
                secondary action the visually dominant one. */}
            <div className="mt-8 border-t border-border pt-6">
              <h3 className="font-headline text-lg font-bold text-foreground mb-3">
                Follow our eco-journey
              </h3>
              <div className="flex justify-center gap-3 lg:justify-start">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    // The Instagram/Facebook glyphs carry their own brand fills,
                    // so only the chip around them is themeable here.
                    className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-muted/50 transition duration-300 hover:border-primary/30 hover:bg-primary/20 active:scale-95"
                    aria-label={`Flora Dine on ${social.name}`}
                  >
                    <Icon name={social.icon} size={20} aria-hidden="true" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DigitalCard;
